/**
 * Asset Value Chart component using Recharts
 */

import type { GoldAsset } from '@/types/asset'
import type { GoldPriceHistory } from '@/types/price'
import type { SaleTransaction } from '@/types/sale'
import { formatVND } from '@/utils/currencyUtils'
import { formatLocalDate } from '@/utils/dateUtils'
import { calculatePortfolioSnapshots } from '@/utils/portfolioUtils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { IconChartLine } from '@tabler/icons-react'
import { useMemo, useState, useEffect } from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface AssetValueChartProps {
  assets: GoldAsset[]
  sales: SaleTransaction[]
  priceHistory: GoldPriceHistory[]
  startDate?: string
  endDate?: string
}

export function AssetValueChart({
  assets,
  sales,
  priceHistory,
  startDate,
  endDate,
}: AssetValueChartProps) {
  const chartData = useMemo(() => {
    let snapshots = calculatePortfolioSnapshots(assets, sales, priceHistory)

    // Filter by date range if provided
    if (startDate || endDate) {
      snapshots = snapshots.filter(snapshot => {
        const date = new Date(snapshot.date)
        if (startDate && date < new Date(startDate)) return false
        if (endDate && date > new Date(endDate)) return false
        return true
      })
    }

    // Limit to 1000 data points for performance
    if (snapshots.length > 1000) {
      const step = Math.ceil(snapshots.length / 1000)
      snapshots = snapshots.filter((_, index) => index % step === 0)
    }

    return snapshots.map(snapshot => ({
      date: formatLocalDate(snapshot.date, 'dd/MM/yyyy'),
      dateValue: snapshot.date,
      totalValue: snapshot.totalValue,
    }))
  }, [assets, sales, priceHistory, startDate, endDate])

  // Check if mobile
  const [isMobile, setIsMobile] = useState(false)
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean
    payload?: Array<{
      value: number
      payload: { date: string; value: number }
    }>
  }) => {
    if (active && payload && payload.length && payload[0]) {
      const firstPayload = payload[0]
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
          <p className="font-semibold text-foreground mb-2">{firstPayload.payload.date}</p>
          <p className="text-sm text-primary">Giá trị: {formatVND(firstPayload.value)}</p>
        </div>
      )
    }
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Biểu đồ Giá trị Tài sản</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IconChartLine className="h-12 w-12" stroke={1.5} />
              </EmptyMedia>
              <EmptyTitle>Không có dữ liệu để hiển thị</EmptyTitle>
              <EmptyDescription>
                Thêm tài sản để xem biểu đồ giá trị tài sản
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ResponsiveContainer width="100%" height={300} className="sm:h-[400px]">
            <LineChart 
              data={chartData} 
              margin={{ 
                top: 5, 
                right: isMobile ? 10 : 30, 
                left: isMobile ? 0 : 20, 
                bottom: isMobile ? 60 : 5 
              }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="date"
                angle={isMobile ? -90 : -45}
                textAnchor={isMobile ? "middle" : "end"}
                height={isMobile ? 100 : 80}
                interval="preserveStartEnd"
                className="text-muted-foreground"
                tick={{ fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis
                tickFormatter={value => formatVND(value)}
                width={isMobile ? 60 : 100}
                className="text-muted-foreground"
                tick={{ fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="totalValue"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                name="Giá trị tài sản"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
